import ConstValues from 'utils/const';
import immutableStore from 'utils/immutableStore';
import alt from 'components/Dispatcher';
import {List, Map, Record} from 'immutable';
import AdminEditUserActions from 'actions/admin/AdminEditUserActions';
import EditUser, {
  EditUserIntegration,
  USER_CAN_VIEW_DEPT_STATS,
  USER_DASHBOARD_MODE_CAN_VIEW_DEPT_STATS,
  USER_DASHBOARD_MODE_ROLE_LEVEL,
  UserPermissions,
} from 'records/EditUser';
import {
  ADMIN_EDIT_USER_FIELD_ACTIVE,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_GLOBALLY,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_DEPARTMENTS,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_TEAMS,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_DEPARTMENTS,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS,
  ADMIN_EDIT_USER_FIELD_CAN_TWO_WAY_SYNC,
  ADMIN_EDIT_USER_FIELD_DEPARTMENT_STAT,
  ADMIN_EDIT_USER_FIELD_EMAIL,
  ADMIN_EDIT_USER_FIELD_FIRSTNAME,
  ADMIN_EDIT_USER_FIELD_HIDDEN,
  ADMIN_EDIT_USER_FIELD_LASTNAME,
  ADMIN_EDIT_USER_FIELD_PERMISSION,
  ADMIN_EDIT_USER_FIELD_ROLE,
  ADMIN_EDIT_USER_FIELD_TEAM,
  ADMIN_EDIT_USER_FIELD_TEAMS_VISIBLE,
  ADMIN_EDIT_USER_FIELD_TIMEZONE,
  ADMIN_EDIT_USER_LANGUAGE_OPTION,
} from 'utils/const/adminEditUserFields';
import {browserHistory} from 'react-router';
import AdminFormDataStore from 'stores/admin/AdminFormDataStore';
import AuthStore from 'stores/AuthStore';
import ToastStore from '@store/toast/ToastStore';
import AuthActions from 'actions/AuthActions';
import {EMAIL_REGEX} from 'utils/regexValidators';
import {Permission} from 'utils/hasPermission';

/**
 * Minimum set of required lists of fields to create/update a user.
 * See https://execvision.myjetbrains.com/youtrack/issue/EV-15145#focus=Comments-112-107433.0-0 for details
 * @type {{userInfo: string[]}}
 */
const REQUIRED_FORM_FIELDS = {
  userInfo: [
    ADMIN_EDIT_USER_FIELD_FIRSTNAME,
    ADMIN_EDIT_USER_FIELD_LASTNAME,
    ADMIN_EDIT_USER_FIELD_EMAIL,
    ADMIN_EDIT_USER_FIELD_TEAM,
    ADMIN_EDIT_USER_FIELD_TIMEZONE,
    ADMIN_EDIT_USER_FIELD_ROLE,
    ADMIN_EDIT_USER_FIELD_PERMISSION,
  ],
};
/**
 * All the possible view-related permissions that form the dropdown for the admin/manager to pick the permission for a
 * editing user
 * @type {string}
 */
export const VIEW_SELF = 'VIEW_SELF';
export const VIEW_ALL_DEPARTMENT = 'VIEW_ALL_DEPARTMENT';
export const VIEW_ALL_ACCOUNT = 'VIEW_ALL_ACCOUNT';
export const VIEW_TEAM = 'VIEW_TEAM';
export const ALL_VIEW_PERMISSIONS = [VIEW_SELF, VIEW_ALL_DEPARTMENT, VIEW_ALL_ACCOUNT, VIEW_TEAM];

const FormState = Record({
  // General error message, not specific to any field
  generalError: '',
  // Maps field names to their error messages
  fieldErrors: Map(),
  // Maps field names to their current values. This is 1-to-1 mapped to the user endpoint's response as an object. See
  // https://execvision.myjetbrains.com/youtrack/issue/EV-15145#focus=Comments-112-107433.0-0 for details
  fieldValues: new EditUser(),
  // Maps field names to their touched states
  fieldTouched: Map(),
  // Maps field names to their initial values
  fieldInitialValues: Map(),
  // Controls if all fields are touched or not. Set to true when submitting
  allTouched: false,
  // Marked as true if any field changed its value from the initial load
  dirty: false,
});

const State = Record({
  // Inform the store's component about whether this is before the initial loading of the store or not. If this is the
  // initial load of the entire page, then we don't render the form until we get the user data to edit or we render the
  // form with initial values if it's a form that creates a user
  isInitialLoad: true,
  // Inform the store's component about whether a request (GET/PUT/etc.) is in progress or not
  isLoading: false,
  // Inform the store's component about whether a request (GET/PUT/etc.) failed or not
  failedLoading: false,
  // Inform the store's component about the current form's state
  formState: FormState(),
  // Corresponds to whether the current form is being submitted or not
  isSubmitLoading: false,
  // Generate error to be shown at the top of the page
  generalError: undefined,
  // remember if can share triggers globally to prevent automatic changes, paired with user role
  changedCanShareTriggersGlobally: false,
});

/**
 * Stores the states of the form on the "User - Edit" page
 */
@immutableStore
class AdminEditUserStore {
  /**
   * Construct a vanilla AdminEditUserStore
   */
  constructor() {
    this.state = State();
    this.bindActions(AdminEditUserActions);
    this.exportPublicMethods({validateFieldValues: this.validateFieldValues.bind(this)});
  }

  /**
   * Triggered when an admin wants to use a blank form to create a user
   */
  onReset() {
    this.setState(State({isInitialLoad: false}));
  }

  /**
   * Triggered by AdminEditUserActions.get to prepare the form for loading a user
   */
  get() {
    this.setState({isLoading: true});
  }

  /**
   * Got the user info for userId from the backend. Now we update this.state with the new user info
   */
  getSuccess(editUser) {
    let fieldInitialValues = this.state.formState.fieldInitialValues;
    if (this.state.isInitialLoad) {
      // Change fieldInitialValues iff this is the initial load of the page so that we update the dirty state of the
      // form based entirely by comparing the fields' values to the initial values later on
      fieldInitialValues = editUser;
    }
    this.setState({
      isInitialLoad: false,
      isLoading: false,
      formState: FormState({
        fieldValues: editUser,
        fieldInitialValues: fieldInitialValues,
      }),
    });
  }

  /**
   * Callback triggered when the admin/manager blurs a field. Once updated, this.state.formState.fieldTouched informs
   * the form whether to render an error message for the field or not
   * @param args {field: <String>}
   */
  onBlurField(args) {
    this.setState(this.state.setIn(['formState', 'fieldTouched', args.field], true));
  }

  /**
   * Callback triggered when the admin changes the dashboard mode dropdown
   * @param dashboardMode {string}
   */
  onChangeDashboardMode(dashboardMode) {
    const editUser = this.state.formState.fieldValues;
    let permissions = List(this.state.formState.fieldValues.permissions.permissions);
    if (dashboardMode === USER_DASHBOARD_MODE_CAN_VIEW_DEPT_STATS) {
      permissions = permissions.filterNot(perm => perm === USER_CAN_VIEW_DEPT_STATS);
      permissions = permissions.push(USER_CAN_VIEW_DEPT_STATS);
    } else if (dashboardMode === USER_DASHBOARD_MODE_ROLE_LEVEL) {
      permissions = permissions.filterNot(perm => perm === USER_CAN_VIEW_DEPT_STATS);
    }
    this.setState(
      this.state.setIn(
        ['formState', 'fieldValues'],
        new EditUser({
          userInfo: editUser.userInfo,
          permissions: new UserPermissions({
            // TODO Replace permissions, departmentStatPermissions, viewTeamPermissions with type=immutable.List
            permissions: permissions.toArray(),
            departmentStatPermissions: editUser.permissions.departmentStatPermissions,
            viewTeamPermissions: editUser.permissions.viewTeamPermissions,
          }),
          integrations: editUser.integrations,
        })
      )
    );
  }

  /**
   * Callback triggered when the admin/manager changes a field
   * @param args  {field: <String>, value: <String>}
   *
   * TODO Split different `changeField` to different groups of fields and handle each group's onChangeField in separate
   * callback function
   */
  onChangeField(args) {
    const accountFormData = AdminFormDataStore.getState();
    let editUser = this.state.formState.fieldValues;
    let editUserPermissions = editUser.permissions;

    if (args.field !== ADMIN_EDIT_USER_FIELD_TEAM && editUser.userInfo.has(args.field)) {
      let permissions = editUserPermissions.permissions;
      let viewTeamPermissions = editUserPermissions.viewTeamPermissions;
      let userInfoToUpdateBy = {};
      userInfoToUpdateBy[args.field] = args.value;
      if (args.field === ADMIN_EDIT_USER_FIELD_ACTIVE) {
        if (!args.value) {
          // If the admin deactivates the user, then we clear all the currently
          // selected teams for the view-teams permission and switch the user's
          // permission from any to view-self
          viewTeamPermissions = [];
          if (!permissions.includes(VIEW_SELF)) {
            permissions.push(VIEW_SELF);
          }
          permissions = permissions.filter(perm => {
            return (perm.startsWith('VIEW') && perm === VIEW_SELF) || !perm.startsWith('VIEW');
          });
        }
      } else if (args.field === ADMIN_EDIT_USER_FIELD_ROLE) {
        if (args.value === 'admin') {
          // Admins can't set view team calls, so if thats set already, then unset it.
          const permIdx = permissions.indexOf(VIEW_TEAM);
          if (permIdx !== -1) {
            permissions.splice(permIdx, 1);
          }
        }
        if (args.value === 'manager' || args.value === 'admin') {
          // By default set "can share triggers globally" for admin or manager
          if (!permissions.includes(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY)) {
            permissions.push(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY);
          }
          // By default remove "Can share triggers with teams" for admin or manager
          if (permissions.includes(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS)) {
            const idx = permissions.indexOf(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS);
            permissions.splice(idx, 1);
          }
        } else if (args.value === 'user') {
          // By default set "Can share triggers with teams" for user
          if (!permissions.includes(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS)) {
            permissions.push(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS);
          }
          // By default remove "Can share triggers globally" for admin or manager
          if (permissions.includes(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY)) {
            const idx = permissions.indexOf(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY);
            permissions.splice(idx, 1);
          }
        }
        // By default set "Can share libraries globally" and
        // "Can perform two way sync on any calls" for all roles
        if (!permissions.includes(ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_GLOBALLY)) {
          permissions.push(ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_GLOBALLY);
        }
        if (!permissions.includes(ADMIN_EDIT_USER_FIELD_CAN_TWO_WAY_SYNC)) {
          permissions.push(ADMIN_EDIT_USER_FIELD_CAN_TWO_WAY_SYNC);
        }
        editUserPermissions = editUserPermissions.set('permissions', permissions);
      } else if (args.field === ADMIN_EDIT_USER_FIELD_HIDDEN) {
        if (!!args.value) {
          // Setting the user archived=true/hidden=true should also set its active=false
          userInfoToUpdateBy[ADMIN_EDIT_USER_FIELD_ACTIVE] = false;
        }
      } else if (args.field === ADMIN_EDIT_USER_FIELD_PERMISSION) {
        // Handle the user's permission change in a special way because it's not stored at the standard top-level
        if (!permissions.includes(args.value)) {
          permissions.push(args.value);
        }
        permissions = permissions.filter(perm => {
          return (perm.startsWith('VIEW') && perm === args.value) || !perm.startsWith('VIEW');
        });
      }
      let userInfo = editUser.userInfo;
      for (const [key, value] of Object.entries(userInfoToUpdateBy)) {
        userInfo = userInfo.set(key, value);
      }
      this.setState(
        this.state.setIn(
          ['formState', 'fieldValues'],
          new EditUser({
            userInfo: userInfo,
            permissions: new UserPermissions({
              permissions: permissions,
              departmentStatPermissions: editUserPermissions.departmentStatPermissions,
              viewTeamPermissions: viewTeamPermissions,
            }),
            integrations: editUser.integrations,
          })
        )
      );
    } else if (args.field === 'permission') {
      // Handle the user's permission change in a special way because it's not stored at the standard top-level
      let permissions = editUser.permissions.permissions;
      if (!permissions.includes(args.value)) {
        permissions.push(args.value);
      }
      permissions = permissions.filter(perm => {
        return (perm.startsWith('VIEW') && perm === args.value) || !perm.startsWith('VIEW');
      });
      this.setState(
        this.state.setIn(
          ['formState', 'fieldValues'],
          new EditUser({
            userInfo: editUser.userInfo,
            permissions: new UserPermissions({
              permissions: permissions,
              departmentStatPermissions: editUserPermissions.departmentStatPermissions,
              viewTeamPermissions: editUserPermissions.viewTeamPermissions,
            }),
            integrations: editUser.integrations,
          })
        )
      );
    } else if (args.field === ADMIN_EDIT_USER_FIELD_TEAMS_VISIBLE) {
      this.setState(
        this.state.setIn(
          ['formState', 'fieldValues'],
          new EditUser({
            userInfo: editUser.userInfo,
            permissions: new UserPermissions({
              permissions: editUserPermissions.permissions,
              departmentStatPermissions: editUserPermissions.departmentStatPermissions,
              viewTeamPermissions: args.value,
            }),
            integrations: editUser.integrations,
          })
        )
      );
    } else if (args.field === ADMIN_EDIT_USER_FIELD_DEPARTMENT_STAT) {
      this.setState(
        this.state.setIn(
          ['formState', 'fieldValues'],
          new EditUser({
            userInfo: editUser.userInfo,
            permissions: new UserPermissions({
              permissions: editUserPermissions.permissions,
              departmentStatPermissions: args.value,
              viewTeamPermissions: editUserPermissions.viewTeamPermissions,
            }),
            integrations: editUser.integrations,
          })
        )
      );
    } else if (
      [
        ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_GLOBALLY,
        ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_DEPARTMENTS,
        ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_TEAMS,
        ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY,
        ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_DEPARTMENTS,
        ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS,
        ADMIN_EDIT_USER_FIELD_CAN_TWO_WAY_SYNC,
      ].includes(args.field)
    ) {
      let permissions = editUserPermissions.permissions;
      if (args.value) {
        // Toggled the permission on
        permissions.push(args.field);
      } else {
        // Toggled the permission off
        const index = permissions.indexOf(args.field);
        if (index > -1) {
          permissions.splice(index, 1);
        }
      }
      let state = this.state.setIn(
        ['formState', 'fieldValues'],
        new EditUser({
          userInfo: editUser.userInfo,
          permissions: new UserPermissions({
            permissions: permissions,
            departmentStatPermissions: editUserPermissions.departmentStatPermissions,
            viewTeamPermissions: editUserPermissions.viewTeamPermissions,
          }),
          integrations: editUser.integrations,
        })
      );
      if (args.field === ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY) {
        state = state.set('changedCanShareTriggersGlobally', true);
      }
      this.setState(state);
    } else if (args.field === ADMIN_EDIT_USER_FIELD_TEAM) {
      const teams = accountFormData.teams;
      const team = teams.filter(team => team.id === args.value).first();
      const department = (accountFormData.departments || List())
        .filter(dept => dept.id === team.department_id)
        .first();
      const userInfo = editUser.userInfo
        .set('teamId', args.value)
        .set('teamName', team.name)
        .set('departmentId', department.id)
        .set('departmentName', department.name);
      this.setState(
        this.state.setIn(
          ['formState', 'fieldValues'],
          new EditUser({
            userInfo: userInfo,
            permissions: editUser.permissions,
            integrations: editUser.integrations,
          })
        )
      );
    } else {
      // args.field and args.value are changes the admin applied towards an integration
      const userIntegrationId = args.field;
      let integrations = this.state.formState.fieldValues.integrations;
      if (userIntegrationId.includes(',')) {
        // userIntegrationId is a comma-separated id of multiple userIntegration.id values
        userIntegrationId.split(',').forEach(id => {
          const newIntegration = new EditUserIntegration({
            integrationId: id,
            externId: args.value,
          });
          // Replace `integrations`'s `id` integration with newIntegration
          integrations = integrations.filterNot(integ => integ.integrationId === id);
          integrations = integrations.push(newIntegration);
        });
      } else {
        // userIntegrationId is the id of 1 integration
        const newIntegration = new EditUserIntegration({
          integrationId: userIntegrationId,
          externId: args.value,
        });
        // Replace `integrations`'s `userIntegrationId` integration with newIntegration
        integrations = integrations.filterNot(
          integration => integration.integrationId === userIntegrationId
        );
        integrations = integrations.push(newIntegration);
      }
      this.setState(
        this.state.setIn(
          ['formState', 'fieldValues'],
          new EditUser({
            userInfo: editUser.userInfo,
            permissions: editUser.permissions,
            integrations: integrations,
          })
        )
      );
    }

    const currentFieldValues = this.state.formState.fieldValues;
    // If any field got its value changed by the admin/manager, we mark `state.formState.dirty = true`
    this.updateDirtyState(currentFieldValues);
    this.validateFieldValues(currentFieldValues, false);
  }

  /**
   * Private function: Update the dirty state of the current form
   * @param fieldValues {EditUser} current EditUser record
   */
  updateDirtyState(fieldValues) {
    const initialValues = this.state.formState.fieldInitialValues;
    const isDirty = !fieldValues.equals(initialValues);
    this.setState(this.state.setIn(['formState', 'dirty'], isDirty));
  }

  /**
   * Validate the field values and return errors if any is found. The errors are expected because the admin/manager
   * might not input a valid value.
   *
   * If the validation passes, it returns {}.
   * If the validation fails, it returns the errors in the form of
   * `{field1: <String>, field2: <String>}`
   *
   * @param fieldValues {EditUser} field values (a EditUser record) to validate
   * @return {Object} Error object
   */
  validateFieldValues(fieldValues, submit) {
    if (submit) {
      this.setState(this.state.setIn(['formState', 'allTouched'], true));
    }

    let errors = Map();
    const userInfo = fieldValues.userInfo;

    // Validate that all required userInfo fields are set
    REQUIRED_FORM_FIELDS.userInfo.forEach(field => {
      let value = null;
      if (field === ADMIN_EDIT_USER_FIELD_PERMISSION) {
        value = fieldValues.permissions.permissions.filter(perm =>
          ALL_VIEW_PERMISSIONS.includes(perm)
        );
      } else {
        value = userInfo.get(field);
        value = value && value.trim ? value.trim() : value;
      }
      if (!value || (Array.isArray(value) && value.length === 0)) {
        errors = errors.set(field, 'Value required');
      }
      if (!!value && value[0] === 'VIEW_TEAM') {
        const viewTeamPermissions = fieldValues.permissions.viewTeamPermissions;
        // TODO Standardize the type of fieldValues.permissions.permissions.viewTeamPermissions
        // to plain JS array instead of a mix of JS array and immutable.List
        if (
          !viewTeamPermissions ||
          viewTeamPermissions.length === 0 ||
          viewTeamPermissions.size === 0
        ) {
          errors = errors.set(ADMIN_EDIT_USER_FIELD_TEAMS_VISIBLE, 'Value required');
        }
      }
    });

    // Validate that the language option isn't empty
    const languages = userInfo.get(ADMIN_EDIT_USER_LANGUAGE_OPTION);
    if (!languages || languages.length === 0) {
      errors = errors.set(ADMIN_EDIT_USER_LANGUAGE_OPTION, 'Value required');
    }

    const email = fieldValues.userInfo.get(ADMIN_EDIT_USER_FIELD_EMAIL, '');
    if (email && !EMAIL_REGEX.test(email)) {
      errors = errors.set(ADMIN_EDIT_USER_FIELD_EMAIL, 'Incorrect email format');
    }

    // Validate that selected departments is not empty when selected
    const hasSelectedDepartmentsPermission = fieldValues.permissions.permissions.some(
      p => p.toLowerCase() === Permission.DEPT_STATS
    );
    const depts = fieldValues.permissions.departmentStatPermissions;
    const noDepartmentsSelected = depts.isEmpty ? depts.isEmpty() : !depts.length;
    if (hasSelectedDepartmentsPermission && noDepartmentsSelected) {
      errors = errors.set(ADMIN_EDIT_USER_FIELD_DEPARTMENT_STAT, 'Value required');
    }

    this.setState(this.state.setIn(['formState', 'fieldErrors'], errors));
    return errors;
  }

  /**
   * Admin has successfully created a new user, so we redirect the admin back to the user list page
   */
  onCreateSuccess() {
    const userInfo = this.state.formState.fieldValues.userInfo;
    setTimeout(() => browserHistory.push('/admin/users'));
    ToastStore.addToast({
      message: `${userInfo.firstName} ${userInfo.lastName} was created!`,
      type: 'success',
    });
  }

  /**
   * Admin has successfully saved the changes to a user. So we redirect the admin back to the parent page.
   * @param resp {EditUser} 200 OK response body from the server
   */
  onSubmitFormSuccess(resp) {
    this.setState(
      this.state
        .set('isSubmitLoading', false)
        .setIn(['formState', 'generalError'], undefined)
        .setIn(['formState', 'fieldValues'], resp)
    );
    const userId = resp.userInfo.userId;
    if (userId === AuthStore.getState().user.id) {
      setTimeout(AuthActions.getUserMeInfo, 2000);
    }
    if (AuthStore.getState().user.isAdmin()) {
      setTimeout(() => browserHistory.push('/admin/users'));
      ToastStore.addToast({
        message: `${resp.userInfo.firstName} ${resp.userInfo.lastName} was saved!`,
        type: 'success',
      });
    } else {
      ToastStore.addToast({
        message: 'Profile updated!',
      });
    }
  }

  /**
   * Admin tried to save the changes to a user but the server returned an error
   * @param resp {Any} 4xx Error response body from the server
   */
  onSubmitFormError(resp) {
    this.setState(setErrorStateFromResponse(resp, this.state));
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }

  /**
   * Admin tried to create a user but the server returned an error
   * @param resp {Any} 4xx Error response body from the server
   */
  onCreateError(resp) {
    this.setState(setErrorStateFromResponse(resp, this.state));
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }

  /**
   * Triggered when the admin wants to submit a form and there are errors on the page
   */
  onCheckFormErrors() {
    const errors = this.state.getIn(['formState', 'fieldErrors']);
    if (!errors.isEmpty()) {
      this.setState(
        this.state
          .setIn(['formState', 'allTouched'], true)
          .setIn(['formState', 'generalError'], 'Please supply values for the highlighted fields.')
      );
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    } else {
      this.setState(this.state.setIn(['formState', 'generalError'], undefined));
    }
  }
}

function setErrorStateFromResponse(resp, state) {
  let result = state.set('isSubmitLoading', false);
  let generalError = 'A server error occurred.';
  if (resp.response.status === ConstValues.httpStatusCodeConflict) {
    generalError =
      'An email address is already taken by another user. Please provide a unique email.';
    const data = resp.response.data;
    const message = 'This email is already in use.';
    // data contains map of email field to the userId of the user who has the email. Value isn't used.
    // One or both might be set.
    if (data['SLACK_EMAIL']) {
      result = result.setIn(['formState', 'fieldErrors', 'slackEmail'], message);
    }
    if (data['EMAIL']) {
      result = result.setIn(['formState', 'fieldErrors', ADMIN_EDIT_USER_FIELD_EMAIL], message);
    }
  }
  return result.setIn(['formState', 'generalError'], generalError);
}

export default alt.createStore(AdminEditUserStore, 'AdminEditUserStore');
